/**
 * ocr.js — the scanned lane: pages that are pictures, read into the SAME shape
 * a text PDF produces.
 *
 * WHAT THIS REPLACED. An honest dead end. When a page had no text layer the
 * tool said so clearly — "распознайте их, а потом вернитесь сюда" — and sent
 * the visitor somewhere else. Clear, and still a refusal: the person with a
 * scanned invoice types the same words into the search box as the person with a
 * digital one ("pdf в excel", 13 200/mo), and cannot tell the two apart,
 * because the difference is invisible until upload. Wordstat 2026-08-30 puts
 * the explicitly scanned wording at another ~2 000/mo on its own
 * («таблица из фото» 1 082, «распознать таблицу» 753, «скан в excel» 171).
 *
 * WHY IT IS SO SHORT. Because nothing here is new. PP-OCRv5 already runs on
 * this site (measured 1.5% character error against Tesseract's 11.0%, see
 * ocr-neural/ppocr.js), /pdf-to-word already proved the render→ImageBitmap→read
 * path, and P2XTables does not care where positioned text came from: it works
 * on {text, x, y, w, h} boxes and recovers columns from the whitespace corridors
 * between them. Detection boxes ARE cell boxes for a table, so the recovered
 * grid comes out of the same code that handles a digital PDF — no second
 * pipeline that can drift from the first.
 *
 * WHAT IT CANNOT DO, and does not pretend to: a scan has no vector content, so
 * the drawn-rules source is unavailable and every scanned table is recovered
 * from corridors alone. The result carries source 'ocr', and the interface says
 * "проверьте границы" for it exactly as it does for 'gaps'.
 *
 * Exposed as window.P2XOcr.
 */
(function (root) {
    'use strict';
    if (root.P2XOcr) return;

    /* 1.6 is not decoration: /pdf-to-word measured small type as noticeably
       worse at 1.0, and above 2.0 only the clock moves. */
    var SCALE = 1.6;

    var engine = null, engineLang = null;

    async function ensure(lang, opts) {
        opts = opts || {};
        if (engine && engineLang === lang) return engine;
        if (engine) { try { engine.close(); } catch (e) {} engine = null; }
        if (!root.PPOCR) throw new Error('PPOCR_MISSING');
        engine = await root.PPOCR.open(lang, {
            workerUrl: opts.workerUrl || undefined,
            onProgress: opts.onModelProgress || function () {},
        });
        engineLang = lang;
        return engine;
    }

    /**
     * readPage(pdfjsLib, page, lang, opts) → { items, lines, width, height }
     *
     * The same return shape as P2XReader.readPage, so the caller has one branch
     * instead of two. `lines` is always empty — see the header.
     */
    async function readPage(pdfjsLib, page, eng) {
        var vp = page.getViewport({ scale: SCALE });
        var cv = document.createElement('canvas');
        cv.width = Math.ceil(vp.width);
        cv.height = Math.ceil(vp.height);
        var cx = cv.getContext('2d', { willReadFrequently: true });
        cx.fillStyle = '#fff';
        cx.fillRect(0, 0, cv.width, cv.height);
        await page.render({ canvasContext: cx, viewport: vp }).promise;

        // read() TRANSFERS its first argument to the worker, and a <canvas> is
        // not transferable: postMessage would throw DataCloneError on the very
        // first page. It has to be an ImageBitmap.
        var bmp = await createImageBitmap(cv);
        var res = await eng.read(bmp, {});
        cv.width = cv.height = 1;

        var items = [];
        var lines = res.lines || [];
        for (var i = 0; i < lines.length; i++) {
            var l = lines[i];
            if (!l.text || !l.text.trim()) continue;
            var b = l.box || {};
            // Detection space is y-DOWN from the top of the raster, which is
            // already the space P2XReader hands over (a pdf.js viewport), so
            // only the scale has to come off. Flipping here would put the rows
            // in reverse order and look exactly like "it recognised it wrong".
            items.push({
                text: l.text,
                x: (b.x || 0) / SCALE,
                y: (b.y || 0) / SCALE,
                w: (b.w || 0) / SCALE,
                h: (b.h || 0) / SCALE,
                conf: l.conf,
            });
        }
        return {
            items: items,
            lines: [],
            width: vp.width / SCALE,
            height: vp.height / SCALE,
        };
    }

    function close() {
        if (engine) { try { engine.close(); } catch (e) {} engine = null; engineLang = null; }
    }

    root.P2XOcr = {
        ensure: ensure,
        readPage: readPage,
        close: close,
        /** MB the chosen language still has to download, for the offer text. */
        sizeMb: async function (lang) {
            if (!root.PPOCR) return null;
            try {
                var cached = await root.PPOCR.isCached(lang);
                return cached ? 0 : root.PPOCR.downloadMb(lang, false);
            } catch (e) { return null; }
        },
        SCALE: SCALE,
    };
})(window);
